<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Human Oversight Patterns for GMP AI Workflows — QikSolve',
  description:
    'Practical AI governance oversight patterns for GMP AI workflows, including review gates, dispositions, escalation thresholds, and exception handling.',
});

const page = {
  hero: {
    eyebrow: 'Human oversight for GMP AI',
    title: 'Make human oversight a workflow, not a signature',
    summary:
      'Trustworthy AI governance depends on clear reviewers, acceptance criteria, dispositions, escalation, and closure evidence for AI-assisted quality work.',
    cta: { label: 'Discuss your GMP AI pathway', to: '/contact' },
  },
  intro: {
    title: 'A reviewer needs more than a name',
    content:
      'Human review is meaningful when the reviewer knows what to assess, what evidence to use, what decisions are available, and when to escalate. The workflow should make accountability visible without creating unnecessary friction.',
    items: [
      { title: 'Role suitability', description: 'Match the reviewer role and authority to the impact of the AI-assisted output.' },
      { title: 'Acceptance criteria', description: 'Define what good enough means before the output reaches review.' },
      { title: 'Disposition and closure', description: 'Record accept, reject, rework, escalate, rationale, and closure evidence.' },
    ],
  },
  sections: [
    {
      title: 'Four oversight patterns',
      items: [
        { title: 'Structured review gate', description: 'Use checklist-based criteria and explicit accept, reject, rework, or escalate outcomes.' },
        { title: 'Dual-role review', description: 'Bring technical and quality or compliance perspectives together when the impact warrants it.' },
        { title: 'Escalation by threshold', description: 'Define signals, mandatory escalation owners, and response expectations before uncertainty occurs.' },
        { title: 'Controlled exception review', description: 'Record why the normal path was not followed, apply compensating controls, and complete retrospective review.' },
      ],
    },
    {
      title: 'Evidence that makes review trustworthy',
      content:
        'A useful oversight record connects the workload, output class, reviewer, timestamp, disposition, rationale, escalation outcome, source evidence, and relevant prompt, model, or rules context where required.',
      items: [
        { title: 'Preserve the original', description: 'Keep the original AI output available alongside human amendments and final disposition.' },
        { title: 'Make uncertainty visible', description: 'Record confidence, missing context, exceptions, and unresolved questions rather than hiding them.' },
      ],
    },
  ],
  callout: {
    title: 'Good oversight supports quality and trust',
    content: 'QikSolve helps teams design proportionate review and evidence practices around the GMP process. QxAIOS remains the destination for platform and product-depth questions.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue the decision',
    items: [
      { label: 'Practical, governed AI hub', description: 'Return to the full Pillar 5 governance pathway.', to: '/product/agentic-ai-governance' },
      { label: 'Define the AI output boundary', description: 'Classify the process impact before selecting the oversight pattern.', to: '/product/defining-gmp-boundary-for-ai-outputs' },
      { label: 'Training and enablement', description: 'Build the capability and review habits that sustain governance.', to: '/product/training' },
      { label: 'Scaling GMP AI through existing quality systems', description: 'See how role-based oversight connects to the wider operating model.', to: '/product/scaling-gmp-ai-through-existing-quality-systems' },
    ],
  },
  cta: { title: 'Need a review model for a GMP AI workflow?', content: 'Bring the use case, output, reviewer roles, and evidence questions your team is working through.', label: 'Discuss your pathway', to: '/contact' },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
